import { useCallback, useState } from "react";

function readStoredSidebarOpen(storageKey: string, defaultOpen: boolean): boolean {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw === null) return defaultOpen;
    return raw === "1";
  } catch {
    return defaultOpen;
  }
}

function writeStoredSidebarOpen(storageKey: string, open: boolean) {
  try {
    localStorage.setItem(storageKey, open ? "1" : "0");
  } catch {
    /* ignore quota / private mode */
  }
}

/** Desktop sidebar open state persisted in localStorage (ChatGPT-style collapse). */
export function usePersistedSidebarOpen(storageKey: string, defaultOpen = true) {
  const [desktopOpen, setDesktopOpen] = useState(() => readStoredSidebarOpen(storageKey, defaultOpen));

  const setDesktopOpenPersisted = useCallback(
    (open: boolean) => {
      setDesktopOpen(open);
      writeStoredSidebarOpen(storageKey, open);
    },
    [storageKey],
  );

  const toggleDesktop = useCallback(() => {
    setDesktopOpen((prev) => {
      const next = !prev;
      writeStoredSidebarOpen(storageKey, next);
      return next;
    });
  }, [storageKey]);

  const openDesktop = useCallback(() => setDesktopOpenPersisted(true), [setDesktopOpenPersisted]);
  const closeDesktop = useCallback(() => setDesktopOpenPersisted(false), [setDesktopOpenPersisted]);

  return { desktopOpen, toggleDesktop, openDesktop, closeDesktop };
}
